import React, { useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Navbar from "../components/Navbar";

// ---------- utilidades RUT ----------

const formatRut = (rutIn) => {
  if (!rutIn) return null;
  let raw = String(rutIn).toUpperCase().replace(/[^0-9K]/g, "");
  if (raw.length < 2) return null;

  const cuerpo = raw.slice(0, -1);
  const dv = raw.slice(-1);

  // inserta puntos cada 3 dígitos desde el final
  let cuerpoConPuntos = "";
  let contador = 0;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    cuerpoConPuntos = cuerpo[i] + cuerpoConPuntos;
    contador++;
    if (contador % 3 === 0 && i !== 0) {
      cuerpoConPuntos = "." + cuerpoConPuntos;
    }
  }

  return `${cuerpoConPuntos}-${dv}`;
};
// ---------- meses/semana (ES) ----------
const MONTHS_ES = {
  "ENERO": 0, "FEBRERO": 1, "MARZO": 2, "ABRIL": 3, "MAYO": 4, "JUNIO": 5,
  "JULIO": 6, "AGOSTO": 7, "SEPTIEMBRE": 8, "SETIEMBRE": 8, "OCTUBRE": 9, "NOVIEMBRE": 10, "DICIEMBRE": 11
};
const WEEKDAY_ES = { "DOMINGO": 0, "LUNES": 1, "MARTES": 2, "MIERCOLES": 3, "MIÉRCOLES": 3, "JUEVES": 4, "VIERNES": 5, "SABADO": 6, "SÁBADO": 6 };

const toISO = (d) => d.toISOString().slice(0,10);

// extrae {year, monthIndex} del nombre de archivo (p.ej. "Baika Hijuelas Septiembre 2025 (Respuestas).xlsx")
const parseMonthYearFromFilename = (filename) => {
  if (!filename) return null;
  const base = filename.replace(/\.[^.]+$/,""); // sin extensión
  const upper = base.toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""); // sin tildes
  // busca una palabra de mes + un año de 4 dígitos
  const m = upper.match(new RegExp(`\\b(${Object.keys(MONTHS_ES).join("|")})\\b\\s+(20\\d{2})`));
  if (!m) return null;
  const monthName = m[1]; const year = parseInt(m[2], 10);
  const monthIndex = MONTHS_ES[monthName];
  if (monthIndex == null) return null;
  return { year, monthIndex };
};

// detecta columnas con patrón "Fondo <DiaSemana> <nro>"
const detectDayColsFromHeaders = (headers) => {
  const cols = [];
  const re = /^Fondo\s+([A-Za-zÁÉÍÓÚÑáéíóúñ]+)\s+(\d{1,2})$/i;
  headers.forEach((h, idx) => {
    const s = String(h || "").trim();
    const mm = s.match(re);
    if (mm) {
      const diaNom = mm[1].toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const nro = parseInt(mm[2], 10);
      cols.push({ colIndex: idx, weekdayName: diaNom, dayNumber: nro, label: s });
    }
  });
  return cols;
};

const CargarProgramacion = () => {
  const [fileObj, setFileObj] = useState(null);
  const [rows, setRows] = useState([]); // [{rut, fecha, almuerzo}]
  const [statusMsg, setStatusMsg] = useState("");
  const [parsing, setParsing] = useState(false);
  const [sending, setSending] = useState(false);

  const handleParse = async () => {
    if (!fileObj) { setStatusMsg("Selecciona un archivo Excel."); return; }
    setParsing(true); setStatusMsg("Leyendo archivo…"); setRows([]);

    try {
      // 1) mes/año desde nombre
      const my = parseMonthYearFromFilename(fileObj.name);
      if (!my) {
        setStatusMsg("No pude detectar mes/año en el nombre del archivo (ej.: 'Septiembre 2025'). Renómbralo o ajusta el patrón.");
        setParsing(false); return;
      }

      const buf = await fileObj.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const wsName = wb.SheetNames[0];
      const ws = wb.Sheets[wsName];
      const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true });
      if (!matrix.length) { setStatusMsg("Hoja vacía."); setParsing(false); return; }

      const headers = matrix[0];
      // columna RUT
      const colRut = headers.findIndex(h => String(h||"").trim().toUpperCase() === "RUT");
      if (colRut < 0) { setStatusMsg('No se encontró columna "RUT".'); setParsing(false); return; }

      // columnas "Fondo <Día> <nro>"
      const dayCols = detectDayColsFromHeaders(headers);
      if (!dayCols.length) {
        setStatusMsg('No se detectaron columnas tipo "Fondo Lunes 15".'); setParsing(false); return;
      }

      const out = [];
      for (let r = 1; r < matrix.length; r++) {
        const row = matrix[r];
        if (!row || row.length === 0) continue;

        const rutNorm = formatRut(row[colRut]);
        if (!rutNorm) continue; // ignora RUT vacío o inválido

        for (const dc of dayCols) {
          const almuerzoRaw = row[dc.colIndex];
          if (almuerzoRaw == null || String(almuerzoRaw).trim() === "") continue;

          const day = dc.dayNumber; // 1..31
          const dt = new Date(Date.UTC(my.year, my.monthIndex, day));
          // (opcional) verificar coherencia de día de semana
          const jsDow = dt.getUTCDay(); // 0=Dom … 6=Sab
          const expectDow = WEEKDAY_ES[dc.weekdayName]; // puede ser undefined si header raro
          // si no coincide, igual usamos la fecha por número; podrías loggear una advertencia

          out.push({
            rut: rutNorm,
            fecha: toISO(dt),
            almuerzo: String(almuerzoRaw).trim()
          });
        }
      }

      setRows(out);
      setStatusMsg(`Archivo: ${fileObj.name} — Mes/Año: ${my.monthIndex+1}/${my.year}. Filas preparadas: ${out.length}.`);

    } catch (e) {
      console.error(e);
      setStatusMsg("Error leyendo el archivo. Revisa el formato.");
    } finally {
      setParsing(false);
    }
  };

  const handleUpload = async () => {
    if (!rows.length) return;
    setSending(true); setStatusMsg("Enviando…");

    try {
      const resp = await axios.post(
        "https://casinop2.sanjoseapps.cl/api/programacion_bulk.php",
        { rows, source_filename: fileObj?.name || null },
        { headers: { "Content-Type": "application/json" } }
      );
      if (resp.data?.ok) {
        const ins = resp.data.insertados ?? 0;
        const upd = resp.data.actualizados ?? 0;
        setStatusMsg(`OK. Insertados: ${ins}, actualizados: ${upd}.`);
      } else {
        setStatusMsg(resp.data?.error || "Error al subir.");
      }
    } catch (e) {
      setStatusMsg(e?.response?.data?.error || e.message || "Error de red.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Navbar />
      <section className="section">
        <div className="container">
          <h1 className="title">Cargar programación (Fondo + día + número)</h1>

          <div className="box">
            <div className="field">
              <label className="label">Archivo Excel</label>
              <div className="control">
                <input
                  className="input"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setFileObj(e.target.files?.[0] || null)}
                />
              </div>
              <p className="help">
                El nombre del archivo debe contener el mes y año en español, p.ej. <b>“Septiembre 2025”</b>.
                Encabezados por día en columnas como <b>“Fondo Lunes 15”</b>.
              </p>
            </div>

            <div className="field is-grouped">
              <div className="control">
                <button className={`button is-link ${parsing ? "is-loading" : ""}`} type="button" onClick={handleParse}>
                  Leer archivo
                </button>
              </div>
              <div className="control">
                <button className={`button is-primary ${sending ? "is-loading" : ""}`} type="button" onClick={handleUpload} disabled={!rows.length}>
                  Enviar al servidor
                </button>
              </div>
            </div>

            {statusMsg && <div className="notification is-info" style={{ whiteSpace:"pre-wrap" }}>{statusMsg}</div>}
          </div>

          <div className="box" style={{ overflowX:"auto" }}>
            <h2 className="subtitle">Previsualización</h2>
            <table className="table is-striped is-fullwidth is-hoverable">
              <thead>
                <tr><th>#</th><th>RUT</th><th>Fecha</th><th>Almuerzo</th></tr>
              </thead>
              <tbody>
                {rows.slice(0,1000).map((r,i)=>(
                  <tr key={`${r.rut}-${r.fecha}-${i}`}>
                    <td>{i+1}</td><td>{r.rut}</td><td>{r.fecha}</td><td>{r.almuerzo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length>1000 && <p className="help">Mostrando 1000 de {rows.length} filas.</p>}
          </div>
        </div>
      </section>
    </>
  );
};

export default CargarProgramacion;
